import { useRef } from 'react'

import { FieldValues } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { transformGenerationOptions } from 'app/components/forms/template/GeneralListingFormTemplate/helpers/carInfo.helpers'
import useOptions from 'app/hooks/useOptions'
import {
  getModels,
  getVehicleSeries,
  getVehicleTrims
} from 'app/services/Api/listing'
import { clearFieldValueIfNotFound } from 'app/utils/helpers/form.helpers'

const useProductFieldsOptions = ({
  byMakeName,
  byModelName,
  byGenerationName,
  bySeriesName,
  byTrimName,
  setValue
}: {
  byMakeName?: string
  byModelName?: string
  byGenerationName?: string
  bySeriesName?: string
  byTrimName?: string
  setValue: UseFormSetValue<FieldValues>
}) => {
  const initialized = useRef({
    generation: false,
    series: false
  })

  const { options: modelOptions } = useOptions({
    key: byMakeName,
    enabled: !!byMakeName,
    loadOptions: async () => {
      const resp = await getModels({
        by_make_name: [byMakeName!]
      })

      clearFieldValueIfNotFound({
        name: 'by_model_name',
        value: null,
        setValue,
        data: resp,
        findKey: 'name',
        findValue: byModelName
      })

      return resp
    },
    optionValue: 'name',
    optionLabel: 'name'
  })

  const { options: generationOptions } = useOptions({
    key: byModelName,
    enabled: !!byModelName && !!modelOptions?.length,
    loadOptions: async () => {
      const modelId = modelOptions?.find(
        (model) => model.value === byModelName
      )?.id

      if (!modelId) {
        return [] as ListingGenerationModel[]
      }

      const resp = await transformGenerationOptions([modelId])

      if (initialized.current.generation) {
        clearFieldValueIfNotFound({
          name: 'by_generation_name',
          value: null,
          setValue,
          data: resp,
          findKey: 'name',
          findValue: byGenerationName
        })
      }

      initialized.current.generation = true

      return resp
    },
    optionValue: 'simpleName',
    optionLabel: 'name'
  })

  const { options: seriesOptions } = useOptions({
    key: byGenerationName,
    enabled: !!byGenerationName && !!generationOptions?.length,
    loadOptions: async () => {
      const generationId = generationOptions?.find(
        (generation) => generation?.value === byGenerationName
      )?.id

      if (!generationId) {
        return [] as ListingSeriesModel[]
      }

      const resp = await getVehicleSeries({ byGenerationId: [generationId] })

      if (initialized.current.series) {
        clearFieldValueIfNotFound({
          name: 'by_serie_name',
          value: null,
          setValue
        })
      }
      initialized.current.series = true

      return resp
    },
    optionValue: 'name',
    optionLabel: 'name'
  })

  const { options: trimOptions } = useOptions({
    key: bySeriesName,
    enabled: !!bySeriesName && !!seriesOptions?.length,
    loadOptions: async () => {
      const serieId = seriesOptions?.find(
        (generation) => generation.value === bySeriesName
      )?.id

      if (!serieId) {
        return [] as ListingTrimModel[]
      }

      const resp = await getVehicleTrims({ bySerieId: [serieId] })

      clearFieldValueIfNotFound({
        name: 'by_trim_name',
        value: null,
        setValue,
        data: resp as any,
        findKey: 'name',
        findValue: byTrimName
      })

      return resp
    },
    optionValue: 'name',
    optionLabel: 'name'
  })

  return {
    modelOptions,
    generationOptions,
    seriesOptions,
    trimOptions
  }
}

export default useProductFieldsOptions
